import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import {
	TENSILE_REQUIREMENT_INITIAL_VALUES,
	TensileRequirementByGrade,
} from "../../models/TensileRequirementByGrade";
import {
	getTensileRequirementsByGrade,
	upsertTensileRequirements,
} from "../../repositories/TensileRequirementByGradeRepository";
import { TensileRequirementsByGradeTable } from "./components/TensileRequirementsByGradeTable";

export const useTensileRequirements = () => {
	return useQuery<TensileRequirementByGrade[], Error>({
		queryKey: ["tensileRequirementsByGrade"],
		queryFn: ({ signal }) => getTensileRequirementsByGrade({}, signal),
	});
};

const moment = getMomentTz();

export const TensileRequirementsPage = () => {
	const queryClient = useQueryClient();
	const tempIdCounter = useRef(0);

	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editRev, setEditRev] = useState<number | null>(null);
	const [editEffectiveDate, setEditEffectiveDate] = useState<Moment | null>(
		null,
	);
	const [editComments, setEditComments] = useState<string>("");
	const [editRows, setEditRows] = useState<TensileRequirementByGrade[]>([]);

	const { data: rows, isLoading, error: e, isError } = useTensileRequirements();

	const upsertMutation = useMutation(
		({
			rev,
			effectiveDate,
			comments,
			rows,
		}: {
			rev: number;
			effectiveDate: Date | null;
			comments: string | null;
			rows: TensileRequirementByGrade[];
		}) =>
			upsertTensileRequirements({
				rev,
				effectiveDate: effectiveDate,
				comments,
				rows,
			}),
		{
			onSuccess: async () => {
				setMessage(`Version REV ${editRev} saved with ${editRows.length} rows`);
				setIsEditMode(false);
				setEditRows([]);
				await queryClient.invalidateQueries({
					queryKey: ["tensileRequirementsByGrade"],
				});
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSaving(false),
		},
	);

	const currentVersion = useMemo(() => {
		if (!rows || rows.length === 0) return null;
		return {
			Rev: rows[0].Rev,
			EffectiveDate: rows[0].EffectiveDate,
			Comments: rows[0].Comments,
			TensileRequirementVersionId: rows[0].TensileRequirementVersionId,
		};
	}, [rows]);

	const handleRowUpdate = (
		rowIndex: number,
		updates: Partial<TensileRequirementByGrade>,
	) => {
		setEditRows((prev) => {
			const updated = [...prev];
			if (updated[rowIndex]) {
				updated[rowIndex] = { ...updated[rowIndex], ...updates };
			}
			return updated;
		});
	};

	const handleNewVersion = () => {
		if (!rows) return;

		const newRev = (currentVersion?.Rev ?? 0) + 1;
		const today = new Date().toISOString().slice(0, 10);

		const copiedRows = rows.map((row) => ({
			...row,
			TensileRequirementsByGradeId: null,
			TensileRequirementVersionId: null,
		}));

		setEditRev(newRev);
		setEditEffectiveDate(moment(today));
		setEditComments(currentVersion?.Comments ?? "");
		setEditRows(copiedRows);
		setIsEditMode(true);
	};

	const handleAddRow = () => {
		tempIdCounter.current += 1;
		const newRow: TensileRequirementByGrade = {
			...TENSILE_REQUIREMENT_INITIAL_VALUES,
			SWV: tempIdCounter.current * -1,
		};
		setEditRows((prev) => [...prev, newRow]);
	};

	const handleCancel = () => {
		setIsEditMode(false);
		setEditRows([]);
		setEditRev(null);
		setEditEffectiveDate(null);
		setEditComments("");
	};

	const handleSave = async () => {
		if (!editRev || editRev < 1) {
			setError("REV is required and must be >= 1");
			return;
		}
		if (!editEffectiveDate) {
			setError("Effective Date is required");
			return;
		}

		for (const row of editRows) {
			if (!row.SWV || row.SWV < 0) {
				setError("All rows must have a valid SWV value before saving.");
				return;
			}
		}

		let hasDuplicateSWV = false;
		const swvSet = new Set<number>();
		for (const row of editRows) {
			if (swvSet.has(row.SWV ?? 0)) {
				hasDuplicateSWV = true;
				break;
			}
			swvSet.add(row.SWV ?? 0);
		}

		if (hasDuplicateSWV) {
			setError("SWV values must be unique.");
			return;
		}

		setIsSaving(true);
		upsertMutation.mutate({
			rev: editRev,
			effectiveDate: editEffectiveDate.toDate(),
			comments: editComments || null,
			rows: editRows,
		});
	};

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	const displayedRows = isEditMode ? editRows : rows || [];

	return (
		<>
			<HelmetDexteel title="Tensile Requirements by Grade" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "85vh",
					p: 1,
					gap: 2,
				}}
			>
				<Typography variant="h5" fontWeight={600}>
					Tensile Requirements by Grade
				</Typography>

				{isEditMode && (
					<Box sx={{ display: "flex", gap: 1 }}>
						<Button
							variant="outlined"
							size="small"
							startIcon={<AddIcon />}
							onClick={handleAddRow}
						>
							Add Row
						</Button>
						<Button
							variant="outlined"
							size="small"
							color="error"
							startIcon={<CancelIcon />}
							onClick={handleCancel}
							disabled={isSaving}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							size="small"
							startIcon={
								isSaving ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<SaveIcon />
								)
							}
							onClick={handleSave}
							disabled={isSaving}
						>
							Save
						</Button>
					</Box>
				)}

				<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
					{isEditMode ? (
						<>
							<TextField
								label="REV"
								type="number"
								size="small"
								value={editRev ?? ""}
								onChange={(e) => setEditRev(Number(e.target.value) || null)}
								sx={{ width: 100 }}
							/>
							<DatePicker
								label="Start"
								format="MM/DD/YYYY"
								value={editEffectiveDate}
								onChange={(newValue) => {
									setEditEffectiveDate(newValue);
								}}
								slots={{
									textField: (params) => (
										<TextField {...params} size="small" fullWidth={false} />
									),
								}}
							/>
						</>
					) : (
						<>
							<Typography variant="subtitle1" fontWeight={500}>
								REV {currentVersion?.Rev ?? "-"}
							</Typography>
							<Typography variant="subtitle1" color="text.secondary">
								Effective:{" "}
								{currentVersion?.EffectiveDate
									? new Date(currentVersion.EffectiveDate).toLocaleDateString()
									: "-"}
							</Typography>
						</>
					)}
				</Box>

				<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }} component={Paper}>
					<TensileRequirementsByGradeTable
						rows={displayedRows}
						isLoading={isLoading}
						isEditMode={isEditMode}
						onRowUpdate={handleRowUpdate}
					/>
				</Box>

				{isEditMode ? (
					<TextField
						label="Comments / Footnotes"
						multiline
						minRows={2}
						maxRows={4}
						size="small"
						fullWidth
						value={editComments}
						onChange={(e) => setEditComments(e.target.value)}
					/>
				) : (
					currentVersion?.Comments && (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ whiteSpace: "pre-line" }}
						>
							{currentVersion.Comments}
						</Typography>
					)
				)}

				{!isEditMode && (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							variant="contained"
							size="small"
							startIcon={<AddIcon />}
							onClick={handleNewVersion}
							disabled={isLoading}
						>
							New Version
						</Button>
					</Box>
				)}
			</Box>
			{message && (
				<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
